import React from 'react';
import { View, TouchableOpacity, StyleSheet, Image } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ChatConversation } from '../../types/chats';
import { getRelativeTime } from '../../data/mockChatsData';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';

interface ChatCardProps {
  chat: ChatConversation;
  onPress: () => void;
}

const ChatCard: React.FC<ChatCardProps> = ({ chat, onPress }) => {
  const { theme } = useTheme();
  
  return (
    <TouchableOpacity style={[styles.card, { backgroundColor: theme.card }]} onPress={onPress} activeOpacity={0.7}>
      <View style={styles.avatarContainer}>
        {chat.userAvatar ? (
          <Image source={{ uri: chat.userAvatar }} style={styles.avatar} />
        ) : (
          <View style={[styles.avatar, { backgroundColor: theme.primary }]}>
            <Ionicons name="person" size={24} color="#000000" />
          </View>
        )}
        {chat.isOnline && <View style={[styles.onlineIndicator, { borderColor: theme.card }]} />}
      </View>
      
      <View style={styles.content}>
        <View style={styles.header}>
          <ThemedText style={[styles.userName, { color: theme.text }]} numberOfLines={1}>{chat.userName}</ThemedText>
          <ThemedText style={[styles.time, { color: theme.textTertiary }]}>{getRelativeTime(chat.lastMessageTime)}</ThemedText>
        </View>
        
        <View style={styles.messageRow}>
          <ThemedText 
            style={[
              styles.lastMessage, 
              { color: chat.unreadCount > 0 ? theme.text : theme.textSecondary },
              chat.unreadCount > 0 && styles.unreadMessage
            ]} 
            numberOfLines={1}
          >
            {chat.lastMessage}
          </ThemedText>
          {chat.unreadCount > 0 && (
            <View style={[styles.unreadBadge, { backgroundColor: theme.primary }]}>
              <ThemedText style={[styles.unreadCount, { color: '#000000' }]}>{chat.unreadCount}</ThemedText>
            </View>
          )}
        </View>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    marginHorizontal: 16,
    marginBottom: 12,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  avatarContainer: {
    position: 'relative',
    marginRight: 12,
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
  },
  onlineIndicator: {
    position: 'absolute',
    bottom: 2,
    right: 2,
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#4CAF50',
    borderWidth: 2,
  },
  content: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  userName: {
    fontSize: 16,
    fontWeight: '600',
    flex: 1,
    marginRight: 8,
  },
  time: {
    fontSize: 12,
  },
  messageRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  lastMessage: {
    fontSize: 14,
    flex: 1,
    marginRight: 8,
  },
  unreadMessage: {
    fontWeight: '600',
  },
  unreadBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 6,
  },
  unreadCount: {
    fontSize: 11,
    fontWeight: '600',
  },
});

export default ChatCard;
